
import React from "react";
import { useTranslation } from 'react-i18next';

const AsianAlleySection: React.FC = () => {
  const { t } = useTranslation('home');

  const highlights = [
    { icon: "ri-store-3-line", value: "2", label: t('asianAlley.stats.locations'), sub: t('asianAlley.stats.locationsSub') },
    { icon: "ri-group-line", value: "5,000+", label: t('asianAlley.stats.customers'), sub: t('asianAlley.stats.customersSub') },
    { icon: "ri-box-3-line", value: "500+", label: t('asianAlley.stats.products'), sub: t('asianAlley.stats.productsSub') },
    { icon: "ri-money-euro-circle-line", value: "€500K+", label: t('asianAlley.stats.revenue'), sub: t('asianAlley.stats.revenueSub') },
  ];

  return (
    <section className="py-12 sm:py-16 md:py-24 lg:py-32 bg-gradient-to-b from-black to-gray-950 relative overflow-hidden">
      {/* Decorative circles */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-600/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
          {/* Image column */}
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-48 h-48 bg-yellow-500/10 rounded-full blur-3xl hidden sm:block"></div>
            <div className="relative rounded-2xl overflow-hidden border border-yellow-500/20 shadow-2xl shadow-yellow-500/10">
              <img
                src="https://public.readdy.ai/ai/img_res/935439e00a1eb7aee5012b966a0f583e.jpg"
                alt="Asian Alley Supermarket - Riga Latvia"
                className="w-full h-64 sm:h-80 md:h-96 lg:h-[500px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                <div className="bg-black/70 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-yellow-500/30">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-yellow-500 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-black text-sm sm:text-lg"></i>
                    </div>
                    <div>
                      <p className="text-yellow-400 font-bold text-xs sm:text-sm">Asian Alley Supermarket</p>
                      <p className="text-gray-300 text-xs">
                        Krisjana Barona Iela 96/98, Riga, Latvia
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content column */}
          <div className="text-white">
            <span className="inline-block px-3 py-1 sm:px-4 sm:py-1.5 bg-yellow-500/15 border border-yellow-400/30 rounded-full text-xs text-yellow-300 font-semibold uppercase tracking-wider mb-4 sm:mb-5">
              {t('asianAlley.badge')}
            </span>

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 font-serif leading-tight">
              <span className="bg-gradient-to-r from-yellow-300 to-yellow-500 bg-clip-text text-transparent">
                {t('asianAlley.title')}
              </span>
              <br />
              <span className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl">
                {t('asianAlley.subtitle')}
              </span>
            </h2>

            <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-6">
              {t('asianAlley.description')}
            </p>

            <p
              className="text-gray-400 text-sm leading-relaxed mb-6 sm:mb-8"
              dangerouslySetInnerHTML={{ __html: t('asianAlley.description2') }}
            />

            {/* Highlights grid */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
              {highlights.map((h, i) => (
                <div
                  key={i}
                  className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 rounded-xl p-3 sm:p-4 md:p-5 border border-yellow-500/15 hover:border-yellow-400/30 transition-all"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-yellow-500/20 rounded-lg mb-2 sm:mb-3">
                    <i className={`${h.icon} text-base sm:text-xl text-yellow-400`}></i>
                  </div>
                  <div className="text-lg sm:text-xl md:text-2xl font-bold text-yellow-400 mb-0.5">{h.value}</div>
                  <div className="text-xs sm:text-sm font-semibold text-white">{h.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5 hidden sm:block">{h.sub}</div>
                </div>
              ))}
            </div>

            {/* Call‑out box */}
            <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-5 bg-yellow-500/10 border border-yellow-400/20 rounded-xl">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-yellow-500 rounded-xl flex-shrink-0">
                <i className="ri-lightbulb-line text-black text-base sm:text-xl"></i>
              </div>
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                <strong className="text-yellow-400">{t('asianAlley.callout.title')}</strong>{" "}
                {t('asianAlley.callout.desc')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AsianAlleySection;
